import React, { useState, useEffect, useContext } from 'react';
import TotalLeads from '../../components/Home/TotalLeads';
import TotalContacts from '../../components/Home/TotalContacts';
import OpenTasks from '../../components/Home/OpenTasks';
import TasksDataGrid from '../../components/Tasks/TasksDataGrid';
import useApiManager from '../../utils/Api/apiManager';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { AuthContext } from '../../context/AuthContext';

import { Grid, Box, Paper } from '@mui/material';
import dayjs from "dayjs";

const HomePage = () => {
  const [dashboardDetails, setDashboardDetails] = useState(null);
  const { get } = useApiManager();

  const { userId } = useContext(AuthContext);
  const [filters, setFilters] = useState([{
    fieldName: "ownerId",
    filterOperator: 0,
    fieldValue: userId,
    fieldType: 1,
  }]);

  const [selectedOption, setSelectedOption] = useState(() => {
    return localStorage.getItem("TaskSelectedOption") || "My Tasks";
  });

  const filterMap = {
    "My Tasks": () => [
      {
        fieldName: "ownerId",
        filterOperator: 0,
        fieldValue: userId,
        fieldType: 1,
      },
    ],

    "My Open Tasks": () => [
      {
        fieldName: "ownerId",
        filterOperator: 0,
        fieldValue: userId,
        fieldType: 1,
      },
      {
        fieldName: "Status",
        filterOperator: 1, // not equal
        fieldValue: "3", // completed
        fieldType: 0,
      },
    ],

    "My Closed Tasks": () => [
      {
        fieldName: "ownerId",
        filterOperator: 0,
        fieldValue: userId,
        fieldType: 1,
      },
      {
        fieldName: "Status",
        filterOperator: 0,
        fieldValue: "3",
        fieldType: 0,
      },
    ],

    "My Today's Tasks": () => [
      {
        fieldName: "ownerId",
        filterOperator: 0,
        fieldValue: userId,
        fieldType: 1,
      },
      {
        fieldName: "dueDate",
        filterOperator: 14,
        fieldValue: `${dayjs().format("YYYY-MM-DD")} 00:00:00;${dayjs().format("YYYY-MM-DD")} 23:59:59`,
        fieldType: 2, // assume 2 = date
      },
      {
        fieldName: "Status",
        filterOperator: 1, // not equal
        fieldValue: "3", // completed
        fieldType: 0,
      },
    ],

    "My Tomorrow's Tasks": () => [
      {
        fieldName: "ownerId",
        filterOperator: 0,
        fieldValue: userId,
        fieldType: 1,
      },
      {
        fieldName: "dueDate",
        filterOperator: 14,
        fieldValue: `${dayjs().add(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss")};${dayjs().add(1, "day").endOf("day").format("YYYY-MM-DD HH:mm:ss")}`,
        fieldType: 2,
      },
    ],

    "My Overdue Tasks": () => [
      {
        fieldName: "ownerId",
        filterOperator: 0,
        fieldValue: userId,
        fieldType: 1,
      },
      {
        fieldName: "dueDate",
        filterOperator: 12, // assume 2 = less than
        fieldValue: dayjs().startOf("day").format("YYYY-MM-DD"),
        fieldType: 2,
      },
      {
        fieldName: "Status",
        filterOperator: 1,
        fieldValue: "3",
        fieldType: 0,
      }
    ],

    "All Tasks": () => [],
  };

  const handleChange = (event) => {
    const selected = event.target.value;
    setSelectedOption(selected);

    // Save to localStorage
    localStorage.setItem('TaskSelectedOption', selected);

    const newFilters = filterMap[selected]?.() || [];
    setFilters(newFilters);
  };

  const options = [
    "My Tasks",
    "My Open Tasks",
    "My Closed Tasks",
    "My Today's Tasks",
    "My Tomorrow's Tasks",
    "My Overdue Tasks",
    "All Tasks"
  ];

  const fetchDashboardDetails = async () => {
    //setLoading(true);

    try {
      const dashboardDetails = await get(`/dashboard`);
      setDashboardDetails(dashboardDetails);
    } catch (error) {
      console.error('Error fetching dashboard details:', error);
    } finally {
      //setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardDetails();
    document.title = "Home Page - iLead CRM"
  }, []);

  useEffect(() => {
  const savedOption = localStorage.getItem("TaskSelectedOption") || "My Tasks";
  const initialFilters = filterMap[savedOption]?.() || [];
  setFilters(initialFilters);
}, [userId]);


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 70px)', // Ensures the Box takes the full height of the viewport
        p: 2
      }}
    >
      <Grid container spacing={2}>
        <Grid item lg={4} sm={6} xs={12}>
          <TotalLeads sx={{ height: '100%' }} value={dashboardDetails?.totalLeads || 0} />
        </Grid>
        <Grid item lg={4} sm={6} xs={12}>
          <TotalContacts sx={{ height: '100%' }} value={dashboardDetails?.totalContacts || 0} />
        </Grid>
        <Grid item lg={4} sm={6} xs={12}>
          <OpenTasks sx={{ height: '100%' }} value={dashboardDetails?.totalOpenTasks || 0} />
        </Grid>
      </Grid>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          mt: 2,
          flexGrow: 1, // Allows Paper to grow and fill the remaining space
          overflow: 'hidden', // Prevents unnecessary scrollbars
        }}
      >
        <Box
          sx={{
            flexShrink: 0, // Ensures the header doesn't grow or shrink
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between", // pushes h3 to left and select to right
            gap: 2, // optional spacing
            mb: 1,
          }}
        >
          <h3>Tasks</h3>
          <FormControl size="small" sx={{ width: 170 }}>
            <InputLabel id="task-filter-label">Tasks</InputLabel>
            <Select
              size="small"
              labelId="task-filter-label"
              value={selectedOption}
              label="Tasks"
              onChange={handleChange}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            flexGrow: 1, // Fills the remaining space for the DataGrid
            overflow: 'auto' // Makes the DataGrid scrollable if content overflows
          }}
        >
          <TasksDataGrid filters={filters} />
        </Box>
      </Paper>
    </Box>


  )
};

export default HomePage;
